import { AdminUser, type IAdminRole } from "@src/domain/entities";
import type { IStoragePort } from "@src/domain/ports";
import type { IAdminLoginRepository } from "@src/domain/repositories/auth";
import { PrivateHttp } from "@src/infrastructure/adapters/http";

export interface IAdminLoginResponse {
  adminUserInfo: AdminUserInfo;
  adminRoles: IAdminRole[];
  adminPermissions: string[];
  adminContext: AdminContext;
  adminAccessToken: string;
  adminRefreshToken: string;
}

interface AdminContext {
  type: string;
  brand_id: null;
  is_global_admin: boolean;
}

interface AdminUserInfo {
  sub: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar: null;
}

export class AdminLoginRepository implements IAdminLoginRepository {
  private readonly storage: IStoragePort;
  private readonly ADMIN_USER_INFO = "adminUserInfo";

  constructor(storage: IStoragePort) {
    this.storage = storage;
  }

  async login(forceRefresh = false) {
    if (!forceRefresh) {
      const adminDetails = await this.getAdminDetailsFromCache();
      if (adminDetails) {
        return adminDetails;
      }
    }

    const adminDetailsFromApi = await this.getAdminDetailsFromApi();
    if (!adminDetailsFromApi) {
      return null;
    }
    return adminDetailsFromApi;
  }

  async clearCache(): Promise<void> {
    await this.storage.delete(this.ADMIN_USER_INFO);
  }

  private mapToAdminUser(data: IAdminLoginResponse): AdminUser {
    return new AdminUser({
      sub: data.adminUserInfo.sub,
      email: data.adminUserInfo.email,
      firstName: data.adminUserInfo.firstName,
      lastName: data.adminUserInfo.lastName,
      avatar: data.adminUserInfo.avatar,
      adminRoles: data.adminRoles.map((role: IAdminRole) => ({
        role_key: role.role_key,
        role_name: role.role_name,
        role_description: role.role_description,
      })),
      adminPermissions: data.adminPermissions,
      adminAccessToken: data.adminAccessToken,
      adminRefreshToken: data.adminRefreshToken,
      adminContext: {
        type: data.adminContext.type,
        brand_id: data.adminContext.brand_id,
        is_global_admin: data.adminContext.is_global_admin,
      },
    });
  }

  private async getAdminDetailsFromCache(): Promise<AdminUser | null> {
    const adminDetails = await this.storage.retrieve<IAdminLoginResponse>(
      this.ADMIN_USER_INFO,
    );
    if (!adminDetails) {
      return null;
    }
    return this.mapToAdminUser(adminDetails.value);
  }

  private async getAdminDetailsFromApi(): Promise<AdminUser | null> {
    const response = await new PrivateHttp().post<IAdminLoginResponse>(
      "/admin/login",
    );
    if (!response) {
      return null;
    }
    await this.setAdminDetailsToCache(response);
    return this.mapToAdminUser(response);
  }

  private async setAdminDetailsToCache(
    rawAdminResponse: IAdminLoginResponse,
  ): Promise<void> {
    const cacheDuration = 60 * 10; // 10 minutes
    await this.storage.store(
      { key: this.ADMIN_USER_INFO, value: rawAdminResponse },
      cacheDuration,
    );
  }
}
